<template>
  <div class="landing-admin">
    <!-- Admin Header -->
    <div class="admin-header">
      <div class="header-title">
        <v-icon small>mdi-cog</v-icon>
        <span>Landing Customizer</span>
      </div>
      <div class="header-actions">
        <v-btn icon small @click="resetToDefaults">
          <v-icon small>mdi-refresh</v-icon>
        </v-btn>
        <v-btn icon small @click="saveConfiguration">
          <v-icon small>mdi-content-save</v-icon>
        </v-btn>
      </div>
    </div>

    <v-container fluid class="admin-container">
      <v-row>
        <!-- Left Panel - Customization Controls -->
        <v-col cols="12" md="4" lg="3" class="customization-panel">
          <v-card class="sticky-panel elevation-4">
            <v-card-title>
              <span>Customize Your Landing Page</span>
            </v-card-title>
            
            <v-card-text class="panel-content">
              <!-- Tournament Basic Info -->
              <v-expansion-panels v-model="expandedPanels" multiple>
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <div>
                      <v-icon left small>mdi-information</v-icon>
                      Basic Information
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-text-field
                      v-model="customization.name"
                      label="Tournament Name"
                      outlined
                      dense
                      class="mb-3"
                    ></v-text-field>
                    
                    <v-textarea
                      v-model="customization.subtitle"
                      label="Tournament Subtitle"
                      outlined
                      dense
                      rows="2"
                      class="mb-3"
                    ></v-textarea>
                    
                    <v-text-field
                      v-model="customization.organizer"
                      label="Organizer Name"
                      outlined
                      dense
                      class="mb-3"
                    ></v-text-field>
                    
                    <v-text-field
                      v-model="customization.totalPrize"
                      label="Total Prize Pool"
                      outlined
                      dense
                      prefix="$"
                    ></v-text-field>
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <!-- Color Customization -->
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <div>
                      <v-icon left small>mdi-palette</v-icon>
                      Colors & Theme
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <div class="color-section">
                      <label class="color-label">Primary Color</label>
                      <div class="color-input-group">
                        <v-menu
                          v-model="colorPickers.primary"
                          :close-on-content-click="false"
                          offset-y
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <div class="color-preview" v-bind="attrs" v-on="on">
                              <div class="color-swatch" :style="{ background: customization.primaryColor }"></div>
                              <span>{{ customization.primaryColor }}</span>
                            </div>
                          </template>
                          <v-color-picker
                            v-model="customization.primaryColor"
                            hide-mode-switch
                          ></v-color-picker>
                        </v-menu>
                      </div>
                      <small class="color-hint">Used for buttons, links, and accents</small>
                    </div>

                    <div class="color-section">
                      <label class="color-label">Secondary Color</label>
                      <div class="color-input-group">
                        <v-menu
                          v-model="colorPickers.secondary"
                          :close-on-content-click="false"
                          offset-y
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <div class="color-preview" v-bind="attrs" v-on="on">
                              <div class="color-swatch" :style="{ background: customization.secondaryColor }"></div>
                              <span>{{ customization.secondaryColor }}</span>
                            </div>
                          </template>
                          <v-color-picker
                            v-model="customization.secondaryColor"
                            hide-mode-switch
                          ></v-color-picker>
                        </v-menu>
                      </div>
                      <small class="color-hint">Used for secondary elements</small>
                    </div>

                    <div class="color-section">
                      <label class="color-label">Footer Background</label>
                      <div class="color-input-group">
                        <v-menu
                          v-model="colorPickers.footer"
                          :close-on-content-click="false"
                          offset-y
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <div class="color-preview" v-bind="attrs" v-on="on">
                              <div class="color-swatch" :style="{ background: customization.footerBackground }"></div>
                              <span>{{ customization.footerBackground }}</span>
                            </div>
                          </template>
                          <v-color-picker
                            v-model="customization.footerBackground"
                            hide-mode-switch
                          ></v-color-picker>
                        </v-menu>
                      </div>
                      <small class="color-hint">Footer section background</small>
                    </div>

                    <div class="preset-colors">
                      <label class="color-label">Quick Presets</label>
                      <div class="preset-chips">
                        <v-chip
                          v-for="preset in colorPresets"
                          :key="preset.name"
                          @click="applyColorPreset(preset)"
                          small
                          class="mr-1 mb-1"
                        >
                          {{ preset.name }}
                        </v-chip>
                      </div>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <!-- Images Customization -->
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <div>
                      <v-icon left small>mdi-image</v-icon>
                      Images & Branding
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <!-- Banner Image -->
                    <div class="image-section">
                      <label class="image-label">Hero Banner Image</label>
                      <v-text-field
                        v-model="customization.bannerImage"
                        label="Banner Image URL"
                        outlined
                        dense
                        placeholder="https://example.com/banner.jpg"
                        class="mb-2"
                      ></v-text-field>
                      <div class="image-preview" v-if="customization.bannerImage">
                        <img :src="customization.bannerImage" alt="Banner Preview">
                      </div>
                      <div class="preset-images">
                        <v-chip
                          v-for="(image, index) in bannerPresets"
                          :key="index"
                          @click="customization.bannerImage = image.url"
                          x-small
                          class="mr-1 mb-1"
                        >
                          {{ image.name }}
                        </v-chip>
                      </div>
                    </div>

                    <!-- Logo Image -->
                    <div class="image-section mt-4">
                      <label class="image-label">Organizer Logo</label>
                      <v-text-field
                        v-model="customization.organizerLogo"
                        label="Logo URL"
                        outlined
                        dense
                        placeholder="https://example.com/logo.png"
                        class="mb-2"
                      ></v-text-field>
                      <div class="image-preview logo-preview" v-if="customization.organizerLogo">
                        <img :src="customization.organizerLogo" alt="Logo Preview">
                      </div>
                    </div>

                    <!-- Overlay Settings -->
                    <div class="mt-4">
                      <label class="slider-label">Banner Overlay Opacity</label>
                      <v-slider
                        v-model="customization.overlayOpacity"
                        min="0"
                        max="1"
                        step="0.1"
                        thumb-label
                        class="mt-2"
                      ></v-slider>
                      <small class="color-hint">Adjust overlay darkness for text readability</small>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <!-- Prize Configuration -->
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <div>
                      <v-icon left small>mdi-trophy</v-icon>
                      Prizes
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-text-field
                      v-model="customization.prizes[0]"
                      label="1st Place Prize"
                      outlined
                      dense
                      prefix="$"
                      class="mb-2"
                    ></v-text-field>
                    
                    <v-text-field
                      v-model="customization.prizes[1]"
                      label="2nd Place Prize"
                      outlined
                      dense
                      prefix="$"
                      class="mb-2"
                    ></v-text-field>
                    
                    <v-text-field
                      v-model="customization.prizes[2]"
                      label="3rd Place Prize"
                      outlined
                      dense
                      prefix="$"
                    ></v-text-field>
                  </v-expansion-panel-content>
                </v-expansion-panel>

                <!-- Advanced Settings -->
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <div>
                      <v-icon left small>mdi-tune</v-icon>
                      Advanced Settings
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-text-field
                      v-model="customization.participants"
                      label="Number of Participants"
                      outlined
                      dense
                      suffix="+"
                      class="mb-3"
                    ></v-text-field>
                    
                    <v-text-field
                      v-model="customization.duration"
                      label="Tournament Duration"
                      outlined
                      dense
                      suffix="Days"
                      class="mb-3"
                    ></v-text-field>
                    
                    <v-select
                      v-model="customization.accountType"
                      :items="['Demo Account', 'Live Account', 'Both']"
                      label="Account Type"
                      outlined
                      dense
                    ></v-select>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-card-text>

            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="error" text @click="resetToDefaults">
                Reset
              </v-btn>
              <v-btn color="primary" @click="saveConfiguration">
                <v-icon left>mdi-content-save</v-icon>
                Save Changes
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>

        <!-- Right Panel - Live Preview -->
        <v-col cols="12" md="8" lg="9" class="preview-panel">
          <div class="preview-header">
            <h3>Live Preview</h3>
            <div class="preview-controls">
              <v-btn-toggle v-model="previewMode" mandatory dense>
                <v-btn value="desktop">
                  <v-icon>mdi-monitor</v-icon>
                </v-btn>
                <v-btn value="tablet">
                  <v-icon>mdi-tablet</v-icon>
                </v-btn>
                <v-btn value="mobile">
                  <v-icon>mdi-cellphone</v-icon>
                </v-btn>
              </v-btn-toggle>
            </div>
          </div>
          
          <div class="preview-container" :class="`preview-${previewMode}`">
            <iframe
              :src="previewUrl"
              frameborder="0"
              class="preview-iframe"
              ref="previewFrame"
            ></iframe>
          </div>
        </v-col>
      </v-row>
    </v-container>

    <!-- Save Confirmation Snackbar -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" top right>
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
export default {
  name: 'TournamentLandingAdmin',
  data() {
    return {
      expandedPanels: [0, 1], // Start with basic info and colors expanded
      previewMode: 'desktop',
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',
      colorPickers: {
        primary: false,
        secondary: false,
        footer: false
      },
      customization: {
        // Basic Info
        name: "Global Trading Championship 2024",
        subtitle: "Compete with the best traders worldwide and win amazing prizes",
        organizer: "Premium Broker Ltd.",
        totalPrize: "100,000",
        participants: "500",
        duration: "30",
        accountType: "Demo Account",
        
        // Colors
        primaryColor: "#4CAF50",
        secondaryColor: "#2196F3",
        tertiaryColor: "#FF9800",
        footerBackground: "#f8f9fa",
        
        // Images
        bannerImage: "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=1920&h=600&fit=crop",
        organizerLogo: "https://via.placeholder.com/200x80/333/fff?text=YOUR+LOGO",
        overlayOpacity: 0.5,
        
        // Prizes
        prizes: ["50,000", "30,000", "20,000"]
      },
      // Preset color schemes
      colorPresets: [
        {
          name: "Classic Green",
          primary: "#4CAF50",
          secondary: "#2196F3",
          footer: "#f8f9fa"
        },
        {
          name: "Professional Blue",
          primary: "#1976D2",
          secondary: "#FFC107",
          footer: "#263238"
        },
        {
          name: "Elegant Purple",
          primary: "#7B1FA2",
          secondary: "#00BCD4",
          footer: "#f5f5f5"
        },
        {
          name: "Bold Red",
          primary: "#F44336",
          secondary: "#FF9800",
          footer: "#212121"
        },
        {
          name: "Modern Dark",
          primary: "#00E676",
          secondary: "#FF6F00",
          footer: "#1a1a1a"
        }
      ],
      // Preset banner images
      bannerPresets: [
        {
          name: "Trading Charts",
          url: "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=1920&h=600&fit=crop"
        },
        {
          name: "Financial City",
          url: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920&h=600&fit=crop"
        },
        {
          name: "Stock Market",
          url: "https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?w=1920&h=600&fit=crop"
        },
        {
          name: "Dark Abstract",
          url: "https://images.unsplash.com/photo-1557683316-973673baf926?w=1920&h=600&fit=crop"
        },
        {
          name: "Success Graph",
          url: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1920&h=600&fit=crop"
        }
      ]
    }
  },
  computed: {
    previewUrl() {
      // Build URL with query parameters for customization
      const params = new URLSearchParams({
        preview: 'true',
        config: JSON.stringify(this.customization)
      });
      return `/tournaments/landing-demo?${params.toString()}`;
    }
  },
  watch: {
    customization: {
      deep: true,
      handler() {
        this.updatePreview();
      }
    }
  },
  mounted() {
    // Load saved configuration if exists
    const saved = localStorage.getItem('tournamentLandingConfig');
    if (saved) {
      try {
        this.customization = JSON.parse(saved);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to load saved configuration');
      }
    }
  },
  methods: {
    updatePreview() {
      // Send message to iframe to update the tournament data
      if (this.$refs.previewFrame && this.$refs.previewFrame.contentWindow) {
        this.$refs.previewFrame.contentWindow.postMessage({
          type: 'UPDATE_TOURNAMENT',
          data: this.customization
        }, '*');
      }
    },
    applyColorPreset(preset) {
      this.customization.primaryColor = preset.primary;
      this.customization.secondaryColor = preset.secondary;
      this.customization.footerBackground = preset.footer;
    },
    saveConfiguration() {
      // Save to localStorage (in production, this would save to backend)
      localStorage.setItem('tournamentLandingConfig', JSON.stringify(this.customization));
      
      this.snackbarText = 'Configuration saved successfully!';
      this.snackbarColor = 'success';
      this.snackbar = true;
      
      // Update the preview
      this.updatePreview();
    },
    resetToDefaults() {
      this.customization = {
        name: "Global Trading Championship 2024",
        subtitle: "Compete with the best traders worldwide and win amazing prizes",
        organizer: "Premium Broker Ltd.",
        totalPrize: "100,000",
        participants: "500",
        duration: "30",
        accountType: "Demo Account",
        primaryColor: "#4CAF50",
        secondaryColor: "#2196F3",
        tertiaryColor: "#FF9800",
        footerBackground: "#f8f9fa",
        bannerImage: "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=1920&h=600&fit=crop",
        organizerLogo: "https://via.placeholder.com/200x80/333/fff?text=YOUR+LOGO",
        overlayOpacity: 0.5,
        prizes: ["50,000", "30,000", "20,000"]
      };
      
      this.snackbarText = 'Reset to default values';
      this.snackbarColor = 'info';
      this.snackbar = true;
    }
  }
}
</script>

<style lang="scss" scoped>
.landing-admin {
  height: 100vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: #f5f5f5;
  
  .admin-header {
    height: 48px;
    min-height: 48px;
    background: var(--v-primary-base);
    color: white;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 16px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    
    .header-title {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 14px;
      font-weight: 500;
    }
    
    .header-actions {
      display: flex;
      gap: 8px;
    }
  }
  
  .admin-container {
    flex: 1;
    overflow: hidden;
    padding: 0;
    max-width: 100%;
    height: calc(100vh - 48px);
  }
  
  .customization-panel {
    height: 100%;
    overflow-y: auto;
    background: #fafafa;
    border-right: 1px solid #e0e0e0;
    
    .sticky-panel {
      height: 100%;
      display: flex;
      flex-direction: column;
      
      .panel-content {
        overflow-y: auto;
        flex: 1;
      }
    }
    
    .color-section {
      margin-bottom: 20px;
      
      .color-label {
        display: block;
        font-size: 13px;
        font-weight: 500;
        margin-bottom: 8px;
        color: #666;
      }
      
      .color-input-group {
        display: flex;
        align-items: center;
        gap: 10px;
        
        .color-preview {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 8px 12px;
          border: 1px solid #e0e0e0;
          border-radius: 8px;
          cursor: pointer;
          background: white;
          transition: all 0.2s;
          
          &:hover {
            border-color: #999;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          }
          
          .color-swatch {
            width: 24px;
            height: 24px;
            border-radius: 4px;
            border: 1px solid #ddd;
          }
          
          span {
            font-family: monospace;
            font-size: 13px;
            font-weight: 500;
          }
        }
      }
      
      .color-hint {
        display: block;
        margin-top: 4px;
        color: #999;
        font-size: 11px;
      }
    }
    
    .preset-colors {
      margin-top: 20px;
      padding-top: 20px;
      border-top: 1px solid #e0e0e0;
      
      .preset-chips {
        margin-top: 8px;
      }
    }
    
    .image-section {
      .image-label {
        display: block;
        font-size: 13px;
        font-weight: 500;
        margin-bottom: 8px;
        color: #666;
      }
      
      .image-preview {
        width: 100%;
        height: 120px;
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        overflow: hidden;
        margin-bottom: 10px;
        background: #f5f5f5;
        
        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        
        &.logo-preview {
          height: 60px;
          background: white;
          
          img {
            object-fit: contain;
            padding: 10px;
          }
        }
      }
      
      .preset-images {
        margin-top: 8px;
      }
    }
    
    .slider-label {
      display: block;
      font-size: 13px;
      font-weight: 500;
      color: #666;
    }
  }
  
  .preview-panel {
    height: 100%;
    display: flex;
    flex-direction: column;
    padding: 20px;
    overflow: hidden;
    
    .preview-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;
      padding: 0 10px;
      
      h3 {
        margin: 0;
        font-size: 20px;
        font-weight: 500;
      }
    }
    
    .preview-container {
      flex: 1;
      background: white;
      border-radius: 8px;
      overflow: auto;
      box-shadow: 0 4px 12px rgba(0,0,0,0.1);
      padding: 20px;
      display: flex;
      justify-content: center;
      min-height: 0;
      
      &.preview-desktop {
        .preview-iframe {
          width: 100%;
          max-width: 1440px;
          min-height: 900px;
        }
      }
      
      &.preview-tablet {
        .preview-iframe {
          width: 768px;
          max-width: 100%;
          min-height: 1024px;
        }
      }
      
      &.preview-mobile {
        .preview-iframe {
          width: 375px;
          max-width: 100%;
          min-height: 812px;
        }
      }
      
      .preview-iframe {
        height: 100%;
        border: none;
        border-radius: 4px;
        background: white;
        transition: width 0.3s ease;
      }
    }
  }
}

/* Vuetify Overrides for better styling */
::v-deep {
  .v-expansion-panel-header {
    font-size: 14px;
    font-weight: 500;
    min-height: 48px !important;
    padding: 12px 16px;
    
    > div {
      display: flex;
      align-items: center;
    }
  }
  
  .v-expansion-panel-content__wrap {
    padding: 16px;
  }
  
  .v-text-field--outlined {
    .v-input__control {
      min-height: 40px;
    }
    
    fieldset {
      border-color: #e0e0e0;
    }
  }
  
  .v-color-picker {
    max-width: 250px;
  }
}
</style>